import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Avatar,
    Box,
    Chip,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    Stack,
    Typography,
    Tooltip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { secureLocalStorage } from "store/SecureStorage";
import LogoutSection from 'store/LogoutSection';
import ChangePasswordDialog from 'store/ChangePasswordDialog';
import { IconLogout, IconSettings, IconKey, IconCircleCheck } from '@tabler/icons';

const ProfileSection = () => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const [anchorEl, setAnchorEl] = useState(null);
    const [logoutOpen, setLogoutOpen] = useState(false);
    const [userName, setUsername] = useState('');
    const [empData, setEmpData] = useState({});
    const [contactInfo, setContactInfo] = useState({});
    const [openChangePwd, setOpenChangePwd] = useState(false);

    const open = Boolean(anchorEl);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleOpenLogoutDialog = () => { handleClose(); setLogoutOpen(true); };
    const handleCloseLogoutDialog = () => setLogoutOpen(false);
    const handleClosePwd = () => setOpenChangePwd(false);

    useEffect(() => { fetchUserData(); }, []);

    const fetchUserData = () => {
        try {
            const savedUser = secureLocalStorage.get('_us');
            if (savedUser) setUsername(savedUser.userid || 'User');
            const savedEmployee = secureLocalStorage.get('_em');
            if (savedEmployee) setEmpData(savedEmployee);
            const savedContact = secureLocalStorage.get('_emc');
            if (savedContact && Array.isArray(savedContact) && savedContact.length > 0)
                setContactInfo(savedContact[0]);
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const initials = userName?.slice(0, 2).toUpperCase();

    // Reusable field row component
    const FieldRow = ({ label, value }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: '5px',
                borderBottom: `0.5px solid ${theme.palette.divider}`,
                '&:last-child': { borderBottom: 'none' }
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.primary',
                    textAlign: 'right',
                    maxWidth: '55%',
                    wordBreak: 'break-word'
                }}
            >
                {value || '—'}
            </Typography>
        </Box>
    );

    // Section header label
    const SectionLabel = ({ children }) => (
        <Typography
            sx={{
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                mb: 1
            }}
        >
            {children}
        </Typography>
    );

    return (
        <>
            {/* ── Trigger Chip ── */}
            <Tooltip title="Profile & Settings" placement="bottom">
                <Chip
                    sx={{
                        height: '42px',
                        borderRadius: '999px',
                        px: 0.5,
                        border: `0.5px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                        },
                        '& .MuiChip-label': { px: 1, lineHeight: 0 },
                        '& .MuiChip-icon': { ml: '4px' }
                    }}
                    icon={
                        <Avatar
                            sx={{
                                width: 30,
                                height: 30,
                                fontSize: '11px',
                                fontWeight: 600,
                                bgcolor: theme.palette.primary.main,
                                color: '#fff',
                            }}
                        >
                            {initials}
                        </Avatar>
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontWeight: 500 }}
                            >
                                {userName}
                            </Typography>
                            <IconSettings
                                size={14}
                                stroke={1.5}
                                color={theme.palette.text.secondary}
                            />
                        </Box>
                    }
                    onClick={handleOpen}
                    variant="outlined"
                />
            </Tooltip>

            {/* ── Dropdown Menu ── */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        width: 300,
                        mt: 1,
                        borderRadius: 2,
                        border: `0.5px solid ${theme.palette.divider}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
                        overflow: 'hidden',
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* ── Header ── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 2,
                        borderBottom: `0.5px solid ${theme.palette.divider}`,
                    }}
                >
                    <Avatar
                        sx={{
                            width: 46,
                            height: 46,
                            fontSize: '16px',
                            fontWeight: 600,
                            bgcolor: theme.palette.primary.main,
                            color: '#fff',
                            flexShrink: 0,
                        }}
                    >
                        {initials}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            {empData?.name} {empData?.lastname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {userName} · {empData?.empid}
                        </Typography>
                    </Box>
                </Box>

                {/* ── Scrollable Body ── */}
                <PerfectScrollbar style={{ maxHeight: '280px' }}>
                    {/* Employee Details */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <SectionLabel>Employee</SectionLabel>
                        <FieldRow label="Unique ID" value={empData?.code} />
                        <FieldRow label="First name" value={empData?.name} />
                        <FieldRow label="Last name" value={empData?.lastname} />
                        <FieldRow label="Employee ID" value={empData?.empid} />
                    </Box>

                    <Divider />

                    {/* Contact Information */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <SectionLabel>Contact</SectionLabel>
                        <FieldRow label="Address" value={contactInfo?.addressline1} />
                        <FieldRow label="City" value={contactInfo?.city} />
                        <FieldRow label="District" value={contactInfo?.dist} />
                        <FieldRow label="State" value={contactInfo?.state} />
                        <FieldRow label="Country" value={contactInfo?.country} />
                        <FieldRow label="Pincode" value={contactInfo?.pincode} />
                        <FieldRow label="Phone" value={contactInfo?.primaryphone} />
                        <FieldRow label="Email" value={contactInfo?.emailid} />
                    </Box>
                </PerfectScrollbar>

                <Divider />

                {/* ── Actions ── */}
                <Box sx={{ p: 1.5 }}>
                    {/* Change Password */}
                    <ListItemButton
                        sx={{
                            borderRadius: `${customization.borderRadius}px`,
                            py: 0.8,
                            mb: 0.75,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.dark,
                            border: `0.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                            }
                        }}
                        onClick={() => { handleClose(); setOpenChangePwd(true); }}
                    >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                            <Box
                                sx={{
                                    width: 26, height: 26,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.main,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <IconKey size={13} stroke={2} color="#fff" />
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="caption" fontWeight={600}>
                                    Change password
                                </Typography>
                            }
                        />
                    </ListItemButton>

                    {/* Logout */}
                    <ListItemButton
                        sx={{
                            borderRadius: `${customization.borderRadius}px`,
                            py: 0.8,
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            color: theme.palette.error.dark,
                            border: `0.5px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.15),
                            }
                        }}
                        onClick={handleOpenLogoutDialog}
                    >
                        <ListItemIcon sx={{ minWidth: 34 }}>
                            <Box
                                sx={{
                                    width: 26, height: 26,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.error.main,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <IconLogout size={13} stroke={2} color="#fff" />
                            </Box>
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="caption" fontWeight={600}>
                                    Logout
                                </Typography>
                            }
                        />
                    </ListItemButton>
                </Box>
            </Menu>

            <LogoutSection
                open={logoutOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleCloseLogoutDialog();
                }}
            />

            <ChangePasswordDialog
                open={openChangePwd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') handleClosePwd();
                }}
                userId={userName}
            />
        </>
    );
};

export default ProfileSection;